// AdminShipsPage.jsx
import React, { useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import MainEngineSubsection from './MainEngineSubsection';
import AuxiliaryEngineSubsection from './AuxiliaryEngineSubsection';
import FuelSubsection from './FuelSubsection';
import EmissionsSubsection from './EmissionsSubsection';
import EventsSubsection from './EventsSubsection';
import { vesselTypes, countries } from './dropdownOptions';

const AdminShipsPage = () => {
  const [sectionsOpen, setSectionsOpen] = useState({
    general: true,
    technical: true,
    operations: true,
    events: true,
  });

  // Reset flag for dynamic components
const [resetDynamic, setResetDynamic] = useState(false);

  // Lifted state for dynamic subsections:
  const [mainEngineData, setMainEngineData] = useState({});
  const [auxiliaryEngineData, setAuxiliaryEngineData] = useState([]);
  const [fuelData, setFuelData] = useState([]);
  const [emissionsData, setEmissionsData] = useState([]);
  const [eventsData, setEventsData] = useState([]);

  const toggleSection = (section) => {
    setSectionsOpen(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const arrow = (isOpen) => (isOpen ? '▼' : '►');

  // Handle form submission by merging static and dynamic data.
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Collect static data from the form.
    const formData = new FormData(e.target);
    const staticData = {};
    for (let [key, value] of formData.entries()) {
      staticData[key] = value;
    }

      // Convert checkbox fields from "on" to true, and ensure they are booleans
  const checkboxKeys = [
    "hasKwhCounter", "manualPowerReporting", "hasTorqueMeter", "hasIGGenerator", "hasScrubber", "dualFuel",
    "drydocking", "hullCleaning", "propellerPolishing", "meOverhaul", "uwInspection", "shaftMeterCalibration", "speedLogCalibration"
  ];
  checkboxKeys.forEach(key => {
    // If the key exists and its value is "on" or "true", convert to boolean true,
    // Otherwise, set it to false.
    staticData[key] = staticData[key] === "on" || staticData[key] === "true";
  });
    // Merge static data with dynamic data
    const shipData = {
      ...staticData,
      // Technical Section: dynamic subsections
      mainEngines: mainEngineData.engines || [],
      numberOfMainEngines: mainEngineData.numberOfMainEngines || '',
      auxiliaryEngines: auxiliaryEngineData,
      fuelTanks: fuelData,
      emissions: emissionsData,
      // Events Section:
      events: eventsData,
    };

    console.log("Submitting ship data:", shipData);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/ship`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(shipData),
      });
      if (response.ok) {
        const data = await response.json();
        console.log("Ship saved successfully:", data);
        alert("Ship details saved successfully!");
        e.target.reset();
        // Reset dynamic states
        setMainEngineData({});
        setAuxiliaryEngineData([]);
        setFuelData([]);
        setEmissionsData([]);
        setEventsData([]);
        setResetDynamic(true);
        // Reset the flag after a short delay
        setTimeout(() => setResetDynamic(false), 0);
      } else {
        const errorData = await response.json();
        console.error("Error saving ship:", errorData);
        alert("Error saving ship: " + (errorData.error || "Unknown error"));
      }
    } catch (err) {
      console.error("Error saving ship:", err);
      alert("Error saving ship: " + err.message);
    }
  };

  return (
    <div className="container-fluid px-4 py-3">
      <h2 className="mb-4">Admin - Ships</h2>
      <form onSubmit={handleSubmit}>
        {/* General Information Section */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center bg-light p-2 border">
            <h4 className="mb-0">General Information</h4>
            <button type="button" className="btn btn-link" onClick={() => toggleSection('general')}>
              {arrow(sectionsOpen.general)}
            </button>
          </div>
          {sectionsOpen.general && (
            <div className="p-3 border border-top-0">
              {/* Row 1 */}
              <div className="row mb-3">
                <div className="col-md-3">
                  <label htmlFor="vesselName" className="form-label">Vessel Name</label>
                  <input type="text" className="form-control" id="vesselName" name="vesselName" placeholder="Enter vessel name" />
                </div>
                <div className="col-md-3">
                  <label htmlFor="imoNumber" className="form-label">IMO Number</label>
                  <input type="text" className="form-control" id="imoNumber" name="imoNumber" placeholder="Enter IMO number" />
                </div>
                <div className="col-md-3">
                   <label htmlFor="mmsi" className="form-label">MMSI</label>
                   <input type="text" className="form-control" id="mmsi" name="mmsi" placeholder="Enter MMSI" />
                </div>
                <div className="col-md-3">
                  <label htmlFor="callSign" className="form-label">Call Sign</label>
                  <input type="text" className="form-control" id="callSign" name="callSign" placeholder="Enter call sign" />
                </div>
                <div className="col-md-3">
                  <label htmlFor="vesselType" className="form-label">Vessel Type</label>
                  <select
                    id="vesselType"
                    name="vesselType"
                    className="form-control"
                    defaultValue=""
                  >
                    <option value="" disabled>— Select Vessel Type —</option>
                    {vesselTypes.map(type => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {/* Row 2 */}
              <div className="row mb-3">
                <div className="col-md-3">
                  <label htmlFor="email" className="form-label">Email</label>
                  <input type="email" className="form-control" id="email" name="email" placeholder="Enter email" />
                </div>
                <div className="col-md-3">
                  <label htmlFor="dwt" className="form-label">DWT</label>
                  <input type="number" className="form-control" id="dwt" name="dwt" placeholder="Enter DWT" />
                </div>
                <div className="col-md-3">
                  <label htmlFor="dwtDesign" className="form-label">DWT-Design</label>
                  <input type="number" className="form-control" id="dwtDesign" name="dwtDesign" placeholder="Enter DWT-Design" />
                </div>
                <div className="col-md-3">
                  <label htmlFor="eedi" className="form-label">EEDI</label>
                  <input type="number" className="form-control" id="eedi" name="eedi" placeholder="Enter EEDI" />
                </div>
              </div>
              {/* Row 3 */}
              <div className="row mb-3">
                <div className="col-md-3">
                  <label htmlFor="builtYear" className="form-label">Built Year</label>
                  <input type="number" className="form-control" id="builtYear" name="builtYear" placeholder="Enter built year" />
                </div>
                <div className="col-md-3">
                  <label htmlFor="flag" className="form-label">Flag</label>
                  <select
                    id="flag"
                    name="flag"
                    className="form-control"
                    defaultValue=""
                  >
                    <option value="" disabled>— Select Flag —</option>
                    {countries.map(c => (
                        <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
               </div>
                <div className="col-md-3">
                  <label htmlFor="loa" className="form-label">LOA</label>
                  <input type="number" className="form-control" id="loa" name="loa" placeholder="Enter LOA" />
                </div>
                <div className="col-md-3">
                  <label htmlFor="lpp" className="form-label">LPP</label>
                  <input type="number" className="form-control" id="lpp" name="lpp" placeholder="Enter LPP" />
                </div>
              </div>
              {/* Row 4 */}
              <div className="row mb-3">
                <div className="col-md-3">
                  <label htmlFor="displacement" className="form-label">Displacement (Scantling)</label>
                  <input type="number" className="form-control" id="displacement" name="displacement" placeholder="Enter displacement" />
                </div>
                <div className="col-md-3">
                  <label htmlFor="breadth" className="form-label">Breadth (Moulded)</label>
                  <input type="number" className="form-control" id="breadth" name="breadth" placeholder="Enter breadth" />
                </div>
                <div className="col-md-3">
                  <label htmlFor="grossTonnage" className="form-label">Gross Tonnage (mt)</label>
                  <input type="number" className="form-control" id="grossTonnage" name="grossTonnage" placeholder="Enter gross tonnage" />
                </div>
                <div className="col-md-3">
                  <label htmlFor="netTonnage" className="form-label">Net Tonnage (mt)</label>
                  <input type="number" className="form-control" id="netTonnage" name="netTonnage" placeholder="Enter net tonnage" />
                </div>
              </div>
              {/* Row 5 */}
              <div className="row mb-3">
                <div className="col-md-3">
                  <label htmlFor="cargoCapacity" className="form-label">Cargo Capacity (m³)</label>
                  <input type="number" className="form-control" id="cargoCapacity" name="cargoCapacity" placeholder="Enter cargo capacity" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Technical Section */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center bg-light p-2 border">
            <h4 className="mb-0">Technical</h4>
            <button type="button" className="btn btn-link" onClick={() => toggleSection('technical')}>
              {arrow(sectionsOpen.technical)}
            </button>
          </div>
          {sectionsOpen.technical && (
            <div className="p-3 border border-top-0">
              {/* Subsection: Specifications */}
              <div className="mb-3">
                <h5 className="fw-bold">Specifications</h5>
                <div className="row mb-3">
                  <div className="col-md-3">
                    <label htmlFor="blockCoefficient" className="form-label fw-bold">Block Coefficient</label>
                    <input
                      type="number"
                      className="form-control"
                      id="blockCoefficient"
                      name="blockCoefficient"
                      placeholder="Enter block coefficient"
                    />
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="frontAreaProjected" className="form-label fw-bold">Front Area Projected</label>
                    <input
                      type="number"
                      className="form-control"
                      id="frontAreaProjected"
                      name="frontAreaProjected"
                      placeholder="Enter front area"
                    />
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="anemometerHeight" className="form-label fw-bold">Anemomenter Height</label>
                    <input
                      type="number"
                      className="form-control"
                      id="anemometerHeight"
                      name="anemometerHeight"
                      placeholder="Enter height"
                    />
                  </div>
                  <div className="col-md-3">
                    <div className="form-check mt-4">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="hasTorqueMeter"
                        name="hasTorqueMeter"
                      />
                      <label className="form-check-label fw-bold" htmlFor="hasTorqueMeter">
                        Has Torque Meter
                      </label>
                    </div>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-3">
                    <div className="form-check mt-4">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="hasKwhCounter"
                        name="hasKwhCounter"
                      />
                      <label className="form-check-label fw-bold" htmlFor="hasKwhCounter">
                        Has kWh Counter
                      </label>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-check mt-4">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="manualPowerReporting"
                        name="manualPowerReporting"
                      />
                      <label className="form-check-label fw-bold" htmlFor="manualPowerReporting">
                        Manual Power Reporting
                      </label>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="ladenConsumption" className="form-label fw-bold">Laden Consumption</label>
                    <input
                      type="number"
                      className="form-control"
                      id="ladenConsumption"
                      name="ladenConsumption"
                      placeholder="Enter laden consumption"
                    />
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="ballastConsumption" className="form-label fw-bold">Ballast Consumption</label>
                    <input
                      type="number"
                      className="form-control"
                      id="ballastConsumption"
                      name="ballastConsumption"
                      placeholder="Enter ballast consumption"
                    />
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-4">
                    <div className="form-check mt-4">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="hasIGGenerator"
                        name="hasIGGenerator"
                      />
                      <label className="form-check-label fw-bold" htmlFor="hasIGGenerator">
                        Has IG Generator
                      </label>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-check mt-4">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="hasScrubber"
                        name="hasScrubber"
                      />
                      <label className="form-check-label fw-bold" htmlFor="hasScrubber">
                        Has Scrubber
                      </label>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-check mt-4">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="dualFuel"
                        name="dualFuel"
                      />
                      <label className="form-check-label fw-bold" htmlFor="dualFuel">
                        Dual Fuel
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Subsection: Main Engine */}
              <MainEngineSubsection onDataChange={setMainEngineData} reset={resetDynamic} />
              {/* Subsection: Auxiliary Engines */}
              <AuxiliaryEngineSubsection onDataChange={setAuxiliaryEngineData} reset={resetDynamic} />
              {/* Subsection: Fuel */}
              <FuelSubsection onDataChange={setFuelData} reset={resetDynamic} />
              {/* Subsection: Emissions */}
              <EmissionsSubsection onDataChange={setEmissionsData} reset={resetDynamic} />
            </div>
          )}
        </div>

        {/* Operations Section */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center bg-light p-2 border">
            <h4 className="mb-0">Operations</h4>
            <button type="button" className="btn btn-link" onClick={() => toggleSection('operations')}>
              {arrow(sectionsOpen.operations)}
            </button>
          </div>
          {sectionsOpen.operations && (
            <div className="p-3 border border-top-0">
              <div className="row mb-3">
                <div className="col-md-3">
                  <label htmlFor="operationalStatus" className="form-label">Operational Status</label>
                  <input type="text" className="form-control" id="operationalStatus" name="operationalStatus" placeholder="Enter operational status" />
                </div>
                <div className="col-md-3">
                  <label htmlFor="crewSize" className="form-label">Crew Size</label>
                  <input type="number" className="form-control" id="crewSize" name="crewSize" placeholder="Enter crew size" />
                </div>
                <div className="col-md-3">
                  <label htmlFor="areaOfOperation" className="form-label">Area of Operation</label>
                  <input type="text" className="form-control" id="areaOfOperation" name="areaOfOperation" placeholder="Enter area of operation" />
                </div>
                <div className="col-md-3">
                  <label htmlFor="lastVoyage" className="form-label">Last Voyage</label>
                  <input type="date" className="form-control" id="lastVoyage" name="lastVoyage" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Events Section */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center bg-light p-2 border">
            <h4 className="mb-0">Events</h4>
            <button type="button" className="btn btn-link" onClick={() => toggleSection('events')}>
              {arrow(sectionsOpen.events)}
            </button>
          </div>
          {sectionsOpen.events && (
            <div className="p-3 border border-top-0">
              <EventsSubsection onDataChange={setEventsData} reset={resetDynamic} />
            </div>
          )}
        </div>

        <button type="submit" className="btn btn-primary">Save Ship Details</button>
      </form>
    </div>
  );
};

export default AdminShipsPage;
